import { getUrlIdsFromNavigationPath } from "@faire/retailer-shared/categories/navigationPath";
import { BrandPageQueryObj } from "@faire/retailer-shared/search/BrandPageQueryObj";
import { getPageNumber } from "@faire/retailer-shared/search/loggedInBrandLeanProductTilesRequest";
import { ICategory } from "@faire/web-api/indigofair/data/ICategory";
import isNil from "lodash/isNil";

import { findClosestActiveCategoryByUrlIds } from "@faire/retailer/stores/domain/CategoryStore/findByUrlId";

interface ICollectionHidingConditionParams {
  hasQueryOrCategoryFilter: boolean | undefined;
  hasFeaturedCategoryReferrer: boolean | undefined;
  hasSearchReferrer: boolean | undefined;
  hasNonCategoryFilter: boolean | undefined;
  isNotFirstPage: boolean;
}

export class CollectionHidingConditions {
  hasQueryOrCategoryFilter: boolean | undefined;
  hasFeaturedCategoryReferrer: boolean | undefined;
  hasSearchReferrer: boolean | undefined;
  hasNonCategoryFilter: boolean | undefined;
  isNotFirstPage: boolean;

  constructor(params: ICollectionHidingConditionParams) {
    this.hasQueryOrCategoryFilter = params.hasQueryOrCategoryFilter;
    this.hasFeaturedCategoryReferrer = params.hasFeaturedCategoryReferrer;
    this.hasSearchReferrer = params.hasSearchReferrer;
    this.hasNonCategoryFilter = params.hasNonCategoryFilter;
    this.isNotFirstPage = params.isNotFirstPage;
  }

  getReasons = (): string[] => {
    return Object.keys(this).filter(
      (key) => (this as Record<string, unknown>)[key] === true
    );
  };
}

export function getCollectionHidingReasons(
  queryParams: BrandPageQueryObj,
  categories: ICategory[] | undefined
): string[] {
  const { sortBy, q: query, refS: referrerSearch } = queryParams;

  const pageNumber = getPageNumber(queryParams);

  const conditions = new CollectionHidingConditions({
    hasQueryOrCategoryFilter:
      !isNil(query) || isCategoryFilterApplied(queryParams),
    hasFeaturedCategoryReferrer: getHasFeaturedCategoryReferrer(
      queryParams,
      categories
    ),
    hasSearchReferrer: !isNil(referrerSearch) && !sortBy,
    hasNonCategoryFilter:
      isNonCategoryFilterOrNonFeaturedSortApplied(queryParams),
    isNotFirstPage: pageNumber !== 1,
  });

  return conditions.getReasons();
}

const getHasFeaturedCategoryReferrer = (
  queryParams: Pick<BrandPageQueryObj, "sortingHint" | "refC">,
  categories: ICategory[] | undefined
): boolean => {
  const { sortingHint, refC } = queryParams;

  if (!sortingHint || refC === undefined) {
    return false;
  }

  const categoryUrlIds = getUrlIdsFromNavigationPath(refC);
  return (
    findClosestActiveCategoryByUrlIds(categoryUrlIds, categories)
      ?.is_featured === true
  );
};

/*
 * When a non category filter (e.g. delivery window) is applied, we should not
 * display collections -- this is to filter out collections on "All Products"
 * category when a non category filter is applied.
 */
function isNonCategoryFilterOrNonFeaturedSortApplied(
  queryParams: BrandPageQueryObj
): boolean {
  const { filters } = queryParams;
  if (!filters) {
    return false;
  }

  return filters.some(
    (option) =>
      option.split(":")[0] !== "category" && option.split(":")[1] !== "featured"
  );
}

/**
 * This reads off whether a non-trivial category filter is applied from the query param.
 * After migrating to filter sections, any preemptive actions (before BE sends back the
 * filter section) can only infer `filters` parameters.
 */
function isCategoryFilterApplied(queryParams: BrandPageQueryObj): boolean {
  const { filters } = queryParams;

  if (!filters || filters.includes("category:All Products")) {
    return false;
  }

  return filters.some((s) => s.startsWith("category:"));
}
